<template>
  <template v-for="(block, blockIndex) in dataBlocks" :key="blockIndex">
    <div :class="['key-and-value', block.className]" v-for="(value, label) in block.data"
      :key="`${blockIndex}-${label}`">
      <div class="label" v-if="isTranslateAvailable(toSnakeCase(value.label))">
        {{ $t(toSnakeCase(value.label)) }}:
      </div>
      <div class="label" v-else>{{ value.label }}:</div>
      <div>
        <div v-if="isAdjustmentField(value.fieldType)" class="pl-2 adjustment-link"
          @click="onShowAdjustment(label, value)" v-html="getDisplayValue(value.value, value.fieldType)"></div>
        <div v-else-if="isListStringField(value.fieldType) && Array.isArray(value.value)" class="value">
          <div class="label-box" style="margin-bottom: 0px; cursor: pointer;"
            @click="showListStringModal(value.value, value.label)">
            {{ value.value.length }} {{ value.value.length === 1 ? $t('item') : $t('items') }}
          </div>
        </div>
        <div v-else v-linkify
          :class="['value', getDisplayValue(value.value) === MASKED_ANNOTATION ? 'masked-value' : '']"
          v-html="getDisplayValue(value.value, value.fieldType)"></div>
      </div>
    </div>
    <hr v-if="blockIndex < dataBlocks.length - 1" />
  </template>
</template>

<script lang="ts">
import { DATE_TIME, MASKED_ANNOTATION } from '@/utils/constants'
import { Linkify } from '@/utils/directives'
import { ECustomFieldType } from '@/utils/enums'
import { toSnakeCase } from '@/utils/helpers'
import { isEmpty } from '@/utils/typeChecker'
import { IManufacturingPlanUse } from '@/utils/types/barcodeTypes'
import { ILabelDisplay, IListStringItem } from '@/utils/types/commonTypes'
import { format } from 'date-fns'
import { Options, Vue } from 'vue-class-component'

@Options({
  directives: {
    linkify: Linkify,
  },
  name: 'ManufacturingDataBlocks',
  methods: { toSnakeCase },
  emits: ['show-adjustment', 'show-list-string'],
})
export default class ManufacturingDataBlocks extends Vue {
  localeList = Object.freeze(['ja', 'en', 'en-us', 'zh-tw', 'zh-cn'])

  get manufacturingPlanData() {
    return this.$store.getters.manufacturingPlan
  }

  get barcode() {
    return this.$store.state.barcode?.barcode
  }

  get totalProducedAmountData() {
    const totalInitialAmount = this.barcode?.totalInitialAmount || 0
    const stockUnit = this.barcode?.manufacturingPlan?.stockUnit || ''
    const conversionFactor = this.barcode?.serialSetting?.conversionFactor || 0
    const actualStorageUnitQuantity = isEmpty(conversionFactor) ? undefined : totalInitialAmount / conversionFactor
    return {
      label: 'actual_quantity',
      value: this.formatWithStorageUnit(actualStorageUnitQuantity, `${totalInitialAmount} ${stockUnit}`),
      fieldType: ECustomFieldType.TEXT,
    }
  }

  get storageUnit(): string {
    return this.manufacturingPlanData?.storageUnit || ''
  }

  formatWithStorageUnit(storageUnitQuantity: number | undefined, stockUnitDisplay: string): string {
    if (isEmpty(storageUnitQuantity) || isEmpty(this.storageUnit)) {
      return stockUnitDisplay
    }
    return `${Math.ceil(storageUnitQuantity as number)} ${this.storageUnit} (${stockUnitDisplay})`
  }

  formatUsePlanQuantity(stockUnitQuantity: number | undefined, usePlan: IManufacturingPlanUse): string {
    const stockUnitDisplay = `${stockUnitQuantity || '0'}${usePlan.stockUnit || ''}`
    const conversionFactor = usePlan.conversionFactor || 0
    if (isEmpty(stockUnitQuantity) || isEmpty(usePlan.storageUnit) || isEmpty(conversionFactor)) {
      return stockUnitDisplay
    }
    return `${Math.ceil((stockUnitQuantity as number) / conversionFactor)} ${usePlan.storageUnit} (${stockUnitDisplay})`
  }

  get MASKED_ANNOTATION() {
    return MASKED_ANNOTATION
  }

  get manufacturingCompletedDate() {
    if (this.manufacturingPlanData) {
      return {
        MITM_CD: {
          label: 'manufacture_completion_date',
          value: format(new Date(this.manufacturingPlanData.manufactureCompletionDate), DATE_TIME.WITHOUT_SECOND),
          fieldType: ECustomFieldType.TEXT,
        },
      }
    }
  }

  get moData() {
    if (this.manufacturingPlanData && this.totalProducedAmountData) {
      return {
        MITM_CD: {
          label: 'manufactured_item_code',
          value: this.manufacturingPlanData.itemCode || '',
          fieldType: ECustomFieldType.TEXT,
        },
        MITM_NAME: {
          label: 'manufactured_item_name',
          value: this.manufacturingPlanData.itemName || '',
          fieldType: ECustomFieldType.TEXT,
        },
        QPLN: {
          label: 'planned_quantity',
          value: this.formatWithStorageUnit(
            this.manufacturingPlanData.plannedBatchQuantity,
            `${this.manufacturingPlanData.plannedQuantityStockUnit || ''} ${this.manufacturingPlanData.stockUnit || ''}`
          ),
          fieldType: ECustomFieldType.TEXT,
        },
        QACT: {
          label: this.totalProducedAmountData.label,
          value: this.totalProducedAmountData.value,
          fieldType: this.totalProducedAmountData.fieldType,
        },
      }
    }
  }

  get usePlanOfMO() {
    if (this.manufacturingPlanData?.usePlans && Array.isArray(this.manufacturingPlanData.usePlans)) {
      const result: Record<string, ILabelDisplay> = {}
      const filteredUsePlans = this.manufacturingPlanData.usePlans.filter(
        (usePlan: IManufacturingPlanUse) =>
          usePlan.itemCode && (usePlan.itemCode.startsWith('3') || usePlan.itemCode.startsWith('4'))
      )
      filteredUsePlans.forEach((usePlan: IManufacturingPlanUse, index: number) => {
        result[`ITEM_CD_${index}`] = {
          label: 'used_item_code',
          value: usePlan.itemCode || '',
          fieldType: ECustomFieldType.TEXT,
        }
        result[`ITEM_NAME_${index}`] = {
          label: 'used_item_name',
          value: usePlan.itemName || '',
          fieldType: ECustomFieldType.TEXT,
        }
        result[`QPLN_${index}`] = {
          label: 'planned_quantity',
          value: this.formatUsePlanQuantity(usePlan.plannedUsageQuantity, usePlan),
          fieldType: ECustomFieldType.TEXT,
        }
        result[`QACT_${index}`] = {
          label: 'used_quantity',
          value: this.formatUsePlanQuantity(usePlan.totalUsedAmount, usePlan),
          fieldType: ECustomFieldType.TEXT,
        }
      })

      return result
    }
    return {}
  }

  get dataBlocks() {
    const blocks = []
    blocks.push({ className: 'block-1', data: this.manufacturingCompletedDate })
    blocks.push({ className: 'block-2', data: this.moData })
    if (Object.keys(this.usePlanOfMO).length > 0) {
      blocks.push({ className: 'block-3', data: this.usePlanOfMO })
    }
    return blocks
  }

  isTranslateAvailable(key: string) {
    let isFound = false
    if (!this.localeList) return
    this.localeList.forEach((val) => {
      if (this.$te(key, val)) {
        isFound = true
      }
    })
    return isFound
  }

  isAdjustmentField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.ADJUSTMENT
  }

  isListStringField(fieldType?: ECustomFieldType) {
    return fieldType === ECustomFieldType.LIST_STRING
  }

  getDisplayValue(value: string | number | IListStringItem[], fieldType?: ECustomFieldType) {
    if (fieldType === ECustomFieldType.DATE_TIME_DATA && !Array.isArray(value)) {
      return format(new Date(value), DATE_TIME.WITH_SECOND)
    }
    return value
  }

  onShowAdjustment(cfKey: string, item: ILabelDisplay) {
    this.$emit('show-adjustment', cfKey, item)
  }

  showListStringModal(value: IListStringItem[], label: string) {
    this.$emit('show-list-string', value, label)
  }
}
</script>

<style lang="scss" scoped>
.adjustment-link {
  color: blue;
  text-decoration: underline;

  &:hover {
    cursor: pointer;
  }
}

.masked-value {
  color: #ff0000;
}

.key-and-value {
  display: flex;
  margin: 8px 0px;
  align-items: center;
  text-align: left;
  justify-content: flex-start;

  .label {
    margin-right: 8px;
    font-weight: 600;
    white-space: nowrap;
  }

  .value {
    width: 100%;
  }
}

.label-box {
  margin-bottom: 12px;
  display: inline-block;
}
</style>
